import { useState } from 'react';
import { useStore } from '@tanstack/react-form';
import { useSavePlanMutation } from '@teachur/http-client';
import type { DegreePlan } from '@teachur/http-client';

import { useAppForm } from '@new/common/hooks/form';

import { toSavePayload } from '../lib/planView';

export interface PlanDraft extends DegreePlan {
  saved: boolean;
}

const fromSavedPlan = (plan: DegreePlan): PlanDraft => ({ ...plan, saved: true });
const fromSuggestion = (plan: DegreePlan): PlanDraft => ({ ...plan, saved: false });
const markSaved = (plan: PlanDraft): PlanDraft => ({ ...plan, saved: true });

export interface PlanFormValues {
  plans: PlanDraft[];
}

export const usePlanForm = (initialPlans: DegreePlan[]) => {
  const saveMutation = useSavePlanMutation();
  const [defaultValues] = useState<PlanFormValues>(() => ({
    plans: initialPlans.map(fromSavedPlan),
  }));

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const chosenPlan = value.plans[value.plans.length - 1];
      if (!chosenPlan) {
        return;
      }
      await saveMutation.mutateAsync({
        degreeId: chosenPlan.degree.id,
        plans: value.plans.map((plan) => ({
          degreeId: plan.degree.id,
          ...toSavePayload(plan.groups),
        })),
      });
      form.reset({ plans: value.plans.map(markSaved) }, { keepDefaultValues: true });
    },
  });

  const plans = useStore(form.store, (state) => state.values.plans);
  const isDirty = useStore(form.store, (state) => state.isDirty);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  return {
    plans,
    isDirty,
    isSubmitting,
    hasUnsavedChanges: () => form.state.isDirty,
    setPlans: (next: PlanDraft[]) => form.setFieldValue('plans', next),
    applySuggestion: (suggested: DegreePlan[]) =>
      form.setFieldValue('plans', suggested.map(fromSuggestion)),
    save: async (): Promise<boolean> => {
      try {
        await form.handleSubmit();
        return form.state.isSubmitSuccessful;
      } catch {
        return false;
      }
    },
  };
};
